import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../utils';
import { CountryDropdown } from 'react-country-region-selector';
import { Link } from 'react-router-dom';
import PasswordField from './PasswordField';

const Signup = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [country, setCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [invitationToken, setInvitationToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [signupComplete, setSignupComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const invitation = fragment.get('invite');
    if (invitation) {
      setInvitationToken(invitation);
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  const formatApiError = (detail) => {
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || 'Invalid signup information').join('. ');
    }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message;
    }
    return typeof detail === 'string' ? detail : 'We could not create your account. Check the invitation and form, then try again.';
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (submissionInFlight.current || signupComplete) return;
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8 || !/[0-9]/.test(password) || !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError('Password must be at least 8 characters and include a number and special character.');
      return;
    }

    if (!/^\+\d{9,15}$/.test(phoneNumber.trim())) {
      setError('Phone number must use international format, for example +35621234567.');
      return;
    }

    submissionInFlight.current = true;
    setSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/signup`, {
        username,
        email,
        password,
        country,
        phone_number: phoneNumber,
        tos_accepted: tosAccepted,
        invitation_token: invitationToken,
      });
      setMessage(response.data.message);
      setSignupComplete(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  const proceedToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-950 sm:px-6 sm:py-12">
      <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="hidden self-center text-white lg:block">
          <Link to="/" className="text-sm font-bold text-cyan-300">← Back to Pinngro</Link>
          <p className="mt-10 text-sm font-bold uppercase tracking-[0.2em] text-cyan-300">Build your strategy team</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight">Build a bot that brings Practice suggestions to you.</h1>
          <p className="mt-5 leading-7 text-slate-300">Create and secure your account, then connect Alpaca Paper through OAuth so your bot can receive market data.</p>
          <ul className="mt-7 space-y-3 text-sm text-slate-300"><li>✓ Alpaca supplies market data for your bot</li><li>✓ Pinngro Practice uses virtual money</li><li>✓ No Alpaca Paper or Live order is submitted</li></ul>
        </div>
      <div className="w-full rounded-3xl bg-white p-5 shadow-2xl sm:p-8">
        <Link to="/" className="mb-4 inline-block text-sm font-bold text-blue-700 lg:hidden">← Back to Pinngro</Link>
        <h2 className="text-3xl font-black text-gray-900 mb-2">Create your Pinngro account</h2>
        <p className="text-sm text-slate-600">Practice uses virtual money. Creating an account does not place a broker order.</p>
        <ol className="mb-5 mt-4 grid grid-cols-3 gap-2 text-left text-xs font-bold" aria-label="Account setup progress">
          <li className={`rounded-lg p-2 ${signupComplete ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>1. Account</li>
          <li className={`rounded-lg p-2 ${signupComplete ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>2. Verify email</li>
          <li className="rounded-lg bg-slate-100 p-2 text-slate-600">3. Complete 2FA</li>
        </ol>
        {!signupComplete ? (
          <form onSubmit={handleSignup}>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700">Human Beta invitation</label>
              <input
                type="text"
                name="invitation_token"
                autoComplete="one-time-code"
                style={{ WebkitTextSecurity: 'disc' }}
                value={invitationToken}
                onChange={(e) => setInvitationToken(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="Paste the invitation code from your email"
                required
              />
              <p className="mt-1 text-xs text-slate-500">Invitations are email-bound, expire, and can be used once. An invitation does not grant Premium.</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700">Username</label>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="Enter your username"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700">Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="signup-password" className="block text-sm font-bold text-gray-700">Password</label>
              <PasswordField
                id="signup-password"
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                containerClassName="mt-1"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="Enter your password"
                required
              />
              <p className="mt-1 text-xs text-slate-500">At least 8 characters, including a number and special character.</p>
            </div>
            <div className="mb-4">
              <label htmlFor="signup-confirm-password" className="block text-sm font-bold text-gray-700">Confirm Password</label>
              <PasswordField
                id="signup-confirm-password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                containerClassName="mt-1"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="Confirm your password"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700">Country</label>
              <CountryDropdown
                value={country}
                onChange={(val) => setCountry(val)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                priorityOptions={['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'JP']}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700">Phone Number</label>
              <input
                type="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                placeholder="+35621234567"
                required
              />
              <p className="mt-1 text-xs text-slate-500">Use international format, starting with + and the country code.</p>
            </div>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mr-2"
                  required
                />
                <span className="text-sm text-gray-700">
                  I accept the <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                </span>
              </label>
            </div>
            <button
              type="submit"
              disabled={submitting}
              aria-busy={submitting}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition duration-200 disabled:cursor-wait disabled:opacity-60"
            >
              {submitting ? 'Creating account…' : 'Sign Up'}
            </button>
            {submitting && <p role="status" className="mt-2 text-sm text-blue-800">Sending your signup request…</p>}
            {error && <p role="alert" className="mt-2 rounded-lg bg-red-100 p-3 text-sm text-red-800">{error} You can correct the form and try again.</p>}
          </form>
        ) : (
          <div className="text-center">
            <p className="text-green-700 mb-2 font-bold">Account created</p>
            <p className="text-gray-700 mb-4">{message}</p>
            <p className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">Open the verification email, then sign in. Pinngro will guide you through QR or manual authenticator setup and verify your first 6-digit code before granting access.</p>
            <button
              onClick={proceedToLogin}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-200"
            >
              Go to sign in
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Signup;
